import { useEffect, useRef, useState } from 'react';
import { workspaceFetch, selectedBusinessWorkspace } from '../platform/business-workspaces';
import type { ContentData } from './content-media';
import { contentFrames, extensionFromFrames, parseContent } from './content-media';
import './content-media.css';

const root = '/management/product-review/content';
export function contentMediaEnabled() {
  return (
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    (selectedBusinessWorkspace()?.unified === true ||
      new URLSearchParams(window.location.search).get('synMedia') === '1')
  );
}
interface Auth {
  session: string;
  csrf?: string;
}
interface Meta {
  id: string;
  sha256: string;
  mime: string;
  name: string;
  source: string;
  basis: string;
  size: number;
}
export interface ContentRef {
  subjectId: string;
  revision: number;
  digest: string;
  view: 'draft' | 'submitted' | 'published';
  caseId?: string;
  access?: 'product-link';
}
function query(ref: ContentRef) {
  return new URLSearchParams(Object.entries(ref).map(([k, v]) => [k, String(v)])).toString();
}
function headers(auth: Auth) {
  return { 'X-Rescue-Synthetic-Session': auth.session, 'X-Rescue-CSRF': auth.csrf ?? '' };
}
async function checked(response: Response) {
  if (!response.ok) throw new Error(`服务端未确认资料请求（HTTP ${response.status}）`);
  return response;
}
async function hash(bytes: ArrayBuffer) {
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), (x) =>
    x.toString(16).padStart(2, '0'),
  ).join('');
}
async function verifiedBlob(response: Response, expected: string) {
  const blob = await response.blob();
  if ((await hash(await blob.arrayBuffer())) !== expected)
    throw new Error('文件摘要不符，材料不可展示或下载。');
  return blob;
}
type Snapshot = {
  data: ContentData;
  media: Meta[];
  images: Record<string, string>;
  imageErrors: Record<string, string>;
};

/** Bind success AND failure to the exact reference, session and request generation. */
function useSnapshot(owner: '05' | '06', auth: Auth, reference?: ContentRef) {
  const refQuery = reference ? query(reference) : '';
  const [attempt, retry] = useState(0);
  const identity = JSON.stringify([owner, refQuery, auth.session, attempt]);
  const [state, setState] = useState<{ identity: string; value?: Snapshot; error?: string } | null>(
    null,
  );
  useEffect(() => {
    if (!contentMediaEnabled() || !refQuery || !auth.session) return;
    const controller = new AbortController(),
      urls: string[] = [];
    const get = async (url: string) =>
      checked(
        await workspaceFetch(url, {
          headers: { 'X-Rescue-Synthetic-Session': auth.session },
          credentials: 'omit',
          redirect: 'error',
          cache: 'no-store',
          signal: controller.signal,
        }),
      );
    void (async () => {
      try {
        const body = (await (await get(`${root}/${owner}/snapshot?${refQuery}`)).json()) as {
          contentJson: string;
          media: Meta[];
        };
        const data = parseContent(body.contentJson);
        if (
          !Array.isArray(body.media) ||
          body.media.length !== data.media.length ||
          data.media.some(
            (m) => !body.media.some((meta) => meta.id === m.id && meta.sha256 === m.sha256),
          )
        )
          throw new Error('材料与当前修订的关联不符，请重试读取。');
        const images: Record<string, string> = {},
          imageErrors: Record<string, string> = {};
        await Promise.all(
          body.media
            .filter((m) => m.mime.startsWith('image/'))
            .map(async (m) => {
              try {
                const blob = await verifiedBlob(
                  await get(`${root}/${owner}/${m.id}?${refQuery}`),
                  m.sha256,
                );
                if (controller.signal.aborted) return;
                const url = URL.createObjectURL(blob);
                urls.push(url);
                images[m.id] = url;
              } catch (e) {
                imageErrors[m.id] = e instanceof Error ? e.message : '主图不可用';
              }
            }),
        );
        if (!controller.signal.aborted)
          setState({ identity, value: { data, media: body.media, images, imageErrors } });
      } catch (e) {
        if (!controller.signal.aborted)
          setState({ identity, error: e instanceof Error ? e.message : '资料不可用' });
      }
    })();
    return () => {
      controller.abort();
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [identity, owner, refQuery, auth.session]);
  const current = state?.identity === identity ? state : null;
  return { value: current?.value, error: current?.error, retry: () => retry((n) => n + 1) };
}

function MediaCard({
  media,
  image,
  error,
  action,
}: {
  media: Meta;
  image?: string | undefined;
  error?: string | undefined;
  action?: React.ReactNode;
}) {
  const isImage = media.mime.startsWith('image/');
  return (
    <article
      className={`cm-media-card ${isImage ? 'cm-media-card--image' : ''}`}
      aria-label={media.name}
    >
      {isImage ? (
        image ? (
          <a
            className="cm-image-link"
            href={image}
            target="_blank"
            rel="noreferrer"
            aria-label={`查看主图：${media.name}`}
          >
            <img src={image} alt={`资料主图：${media.name}`} />
            <span>查看主图 ↗</span>
          </a>
        ) : (
          <div className="cm-media-placeholder" role="status">
            {error || '主图不可用'}
          </div>
        )
      ) : (
        <div className="cm-file-icon" aria-hidden="true">
          PDF
        </div>
      )}
      <div className="cm-media-info">
        <h4>{media.name}</h4>
        <p className="cm-caption">
          {isImage ? '主图' : 'PDF 附件'} · {Math.max(1, Math.round(media.size / 1024))} KB
        </p>
        <span className="cm-badge">
          {media.basis === 'SYNTHETIC_TEST_ONLY' ? '合成测试材料' : '提交者声明仅本机展示'}
        </span>
        <p className="cm-caption">未经能力验证；来源与展示依据均为声明。</p>
        {action}
        <details className="cm-audit">
          <summary>来源与技术审计</summary>
          <dl>
            <dt>材料来源（提交者声明）</dt>
            <dd>{media.source}</dd>
            <dt>展示依据</dt>
            <dd>{media.basis}</dd>
            <dt>材料 UUID</dt>
            <dd>
              <code>{media.id}</code>
            </dd>
            <dt>SHA-256</dt>
            <dd>
              <code>{media.sha256}</code>
            </dd>
          </dl>
        </details>
      </div>
    </article>
  );
}

/** Original forms retain Owner save; upload success alone never claims a saved revision. */
export function ContentEditor({
  owner,
  auth,
  value,
  onChange,
  onBusy,
  reference,
}: {
  owner: '05' | '06';
  auth: Auth;
  value: string;
  onChange: (json: string) => void;
  onBusy?: (busy: boolean) => void;
  reference?: ContentRef | undefined;
}) {
  const data = parseContent(value),
    saved = useSnapshot(owner, auth, reference);
  const [source, setSource] = useState(''),
    [basis, setBasis] = useState('SYNTHETIC_TEST_ONLY');
  const [file, setFile] = useState<File | null>(null),
    [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(''),
    [error, setError] = useState('');
  const [uploads, setUploads] = useState<Record<string, { meta: Meta; image?: string }>>({});
  const urls = useRef<string[]>([]),
    input = useRef<HTMLInputElement>(null),
    mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      urls.current.forEach(URL.revokeObjectURL);
      onBusy?.(false);
    };
  }, [onBusy]);
  const change = (next: ContentData) => {
    onChange(JSON.stringify(next));
    setMessage('资料已修改，尚未保存。请完成表单并保存本修订。');
  };
  async function upload() {
    if (!file || !source.trim()) {
      setError('请选择文件并填写材料来源。');
      return;
    }
    const mime = file.type;
    if (
      !['image/png', 'image/jpeg', 'application/pdf'].includes(mime) ||
      file.size > (mime === 'application/pdf' ? 10 : 5) * 1024 * 1024
    ) {
      setError('仅支持5 MiB以内PNG/JPEG和10 MiB以内PDF。');
      return;
    }
    const metadata = data.media.map(
      (m) => uploads[m.id]?.meta ?? saved.value?.media.find((x) => x.id === m.id),
    );
    if (
      data.media.length >= 6 ||
      (mime.startsWith('image/') && metadata.some((m) => m?.mime.startsWith('image/'))) ||
      (mime === 'application/pdf' && metadata.filter((m) => m?.mime === mime).length >= 5)
    ) {
      setError('每修订最多1张主图和5份PDF；请先移除要替换的材料。');
      return;
    }
    setBusy(true);
    onBusy?.(true);
    setError('');
    setMessage('');
    try {
      const bytes = await file.arrayBuffer(),
        sha256 = await hash(bytes);
      const params = new URLSearchParams({ name: file.name, mime, source, basis, sha256 });
      const response = await checked(
        await workspaceFetch(`${root}/${owner}?${params}`, {
          method: 'POST',
          headers: {
            ...headers(auth),
            'Content-Type': 'application/octet-stream',
            'Idempotency-Key': crypto.randomUUID(),
          },
          body: bytes,
          credentials: 'omit',
          redirect: 'error',
        }),
      );
      const meta = (await response.json()) as Meta;
      if (meta.sha256 !== sha256 || meta.mime !== mime)
        throw new Error('上传响应与所选文件不符，未关联材料。');
      if (!mounted.current) return;
      const image = mime.startsWith('image/') ? URL.createObjectURL(file) : undefined;
      if (image) urls.current.push(image);
      setUploads((previous) => ({ ...previous, [meta.id]: { meta, ...(image ? { image } : {}) } }));
      change({ ...data, media: [...data.media, { id: meta.id, sha256: meta.sha256 }] });
      setMessage(`“${meta.name}”已上传，尚未保存到资料版本。请保存本修订。`);
      setFile(null);
      if (input.current) input.current.value = '';
    } catch (e) {
      if (mounted.current)
        setError(e instanceof Error ? e.message : '上传结果未确认，请重新读取。');
    } finally {
      if (mounted.current) {
        setBusy(false);
        onBusy?.(false);
      }
    }
  }
  if (!contentMediaEnabled()) return null;
  return (
    <fieldset className="cm-editor" disabled={busy}>
      <legend>版本资料与材料</legend>
      <p className="cm-caption">
        本机合成资料 ·{' '}
        {owner === '05'
          ? '本件装备的实际配置声明，与厂家声明分别保存。'
          : '厂家声明，不代表实测能力。'}
      </p>
      <div className="cm-fields">
        {owner === '06' && (
          <label>
            用途说明（厂家声明）
            <textarea
              value={data.purpose}
              maxLength={4000}
              onChange={(e) => change({ ...data, purpose: e.target.value })}
            />
          </label>
        )}
        <label>
          安全与技术限制
          <textarea
            value={data.limitations}
            maxLength={4000}
            onChange={(e) => change({ ...data, limitations: e.target.value })}
          />
        </label>
      </div>
      <section className="cm-editor-section" aria-label="技术参数编辑">
        <h4>
          技术参数 <span className="cm-caption">声明值</span>
        </h4>
        {data.parameters.length === 0 && <p className="cm-empty">尚未填写技术参数。</p>}
        {data.parameters.map((p, index) => (
          <div className="cm-parameter-input" key={index}>
            {(['name', 'value', 'unit'] as const).map((k) => (
              <label key={k}>
                {k === 'name' ? '参数名称' : k === 'value' ? '参数值' : '单位'} {index + 1}
                <input
                  value={p[k]}
                  maxLength={k === 'value' ? 500 : k === 'name' ? 100 : 50}
                  onChange={(e) =>
                    change({
                      ...data,
                      parameters: data.parameters.map((x, i) =>
                        i === index ? { ...x, [k]: e.target.value } : x,
                      ),
                    })
                  }
                />
              </label>
            ))}
            <button
              type="button"
              onClick={() =>
                change({ ...data, parameters: data.parameters.filter((_, i) => i !== index) })
              }
            >
              移除参数 {index + 1}
            </button>
          </div>
        ))}
        <button
          type="button"
          disabled={data.parameters.length >= 40}
          onClick={() =>
            change({ ...data, parameters: [...data.parameters, { name: '', value: '', unit: '' }] })
          }
        >
          添加资料参数
        </button>
      </section>
      <section className="cm-editor-section" aria-label="图片与附件编辑">
        <h4>
          主图与 PDF 附件 <span className="cm-caption">{data.media.length} / 6</span>
        </h4>
        <p className="cm-caption">
          最多1张主图（PNG/JPEG，5 MiB）、5份PDF（每份10 MiB）。上传后还需保存版本。
        </p>
        {saved.error && (
          <p role="alert" className="cm-error">
            {saved.error}{' '}
            <button type="button" onClick={saved.retry}>
              重试材料读取
            </button>
          </p>
        )}
        <div className="cm-media-grid">
          {data.media.map((m, index) => {
            const upload = uploads[m.id],
              meta = upload?.meta ?? saved.value?.media.find((x) => x.id === m.id);
            const remove = (
              <button
                type="button"
                aria-label={`从本修订移除：${meta?.name ?? `材料 ${index + 1}`}`}
                onClick={() => change({ ...data, media: data.media.filter((x) => x.id !== m.id) })}
              >
                从本修订移除
              </button>
            );
            return meta ? (
              <MediaCard
                key={m.id}
                media={meta}
                image={upload?.image ?? saved.value?.images[m.id]}
                error={saved.value?.imageErrors[m.id]}
                action={remove}
              />
            ) : (
              <article className="cm-media-card" key={m.id}>
                <div className="cm-media-info">
                  <h4>已关联材料 {index + 1}</h4>
                  <p className="cm-caption">
                    {saved.error ? '材料不可读取' : '正在读取已保存的材料信息…'}
                  </p>
                  {remove}
                  <details className="cm-audit">
                    <summary>材料标识与摘要</summary>
                    <p>{m.id}</p>
                    <code>{m.sha256}</code>
                  </details>
                </div>
              </article>
            );
          })}
        </div>
        <div className="cm-upload">
          <div className="cm-fields">
            <label>
              材料来源
              <input
                value={source}
                maxLength={2000}
                onChange={(e) => setSource(e.target.value)}
                placeholder="说明材料由谁提供、如何取得"
              />
            </label>
            <label>
              本机展示依据
              <select value={basis} onChange={(e) => setBasis(e.target.value)}>
                <option value="SYNTHETIC_TEST_ONLY">自行制作的合成测试材料</option>
                <option value="USER_CONFIRMED_LOCAL_USE">提交者确认仅本机展示使用</option>
              </select>
            </label>
          </div>
          <label>
            主图或PDF
            <input
              ref={input}
              type="file"
              accept=".png,.jpg,.jpeg,.pdf"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setError('');
                setMessage('');
              }}
            />
          </label>
          <button type="button" disabled={!file || !source.trim()} onClick={() => void upload()}>
            {busy ? '正在上传…' : '上传所选材料'}
          </button>
        </div>
      </section>
      {error && (
        <p role="alert" className="cm-error">
          {error}
        </p>
      )}
      <p role="status" className="cm-feedback">
        {busy
          ? '上传中，请等待服务器确认。'
          : message || '来源及展示依据为提交者声明，未获得商业使用或认证许可。'}
      </p>
    </fieldset>
  );
}

export function ContentViewer({
  owner,
  auth,
  reference,
  title = '版本资料与材料',
}: {
  owner: '05' | '06';
  auth: Auth;
  reference: ContentRef;
  title?: string;
}) {
  // Remount on identity/session change: late downloads cannot affect the newly selected object.
  return contentMediaEnabled() ? (
    <SnapshotViewer
      key={JSON.stringify([owner, reference, auth.session])}
      owner={owner}
      auth={auth}
      reference={reference}
      title={title}
    />
  ) : null;
}
function SnapshotViewer({
  owner,
  auth,
  reference,
  title,
}: {
  owner: '05' | '06';
  auth: Auth;
  reference: ContentRef;
  title: string;
}) {
  const snapshot = useSnapshot(owner, auth, reference),
    current = snapshot.value;
  const [downloadState, setDownloadState] = useState<{
    id: string;
    error?: string;
    busy?: boolean;
    success?: boolean;
  } | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  async function download(m: Meta) {
    setDownloadState({ id: m.id, busy: true });
    try {
      const r = await checked(
        await workspaceFetch(`${root}/${owner}/${m.id}?${query(reference)}`, {
          headers: headers(auth),
          credentials: 'omit',
          redirect: 'error',
          cache: 'no-store',
        }),
      );
      const blob = await verifiedBlob(r, m.sha256);
      if (!mounted.current) return;
      const url = URL.createObjectURL(blob),
        a = document.createElement('a');
      a.href = url;
      a.download = m.name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setDownloadState({ id: m.id, success: true });
    } catch (e) {
      if (mounted.current)
        setDownloadState({ id: m.id, error: e instanceof Error ? e.message : '材料不可用' });
    }
  }
  return (
    <section className="cm-section" aria-label={title}>
      <div className="cm-section-heading">
        <h3>{title}</h3>
        <span className="cm-badge">
          {reference.view === 'published'
            ? '发布版本'
            : reference.view === 'submitted'
              ? '提交修订'
              : '当前草稿'}{' '}
          {reference.revision}
        </span>
      </div>
      {snapshot.error ? (
        <div role="alert" className="cm-error">
          <p>{snapshot.error}；资料未显示。</p>
          <button type="button" onClick={snapshot.retry}>
            重试资料读取
          </button>
        </div>
      ) : !current ? (
        <p role="status" className="cm-empty">
          读取精确版本资料…
        </p>
      ) : (
        <>
          <dl className="cm-facts">
            {owner === '06' && (
              <div>
                <dt>用途声明</dt>
                <dd>{current.data.purpose || '未填写'}</dd>
              </div>
            )}
            <div>
              <dt>安全与技术限制</dt>
              <dd>{current.data.limitations || '未填写，不代表无限制。'}</dd>
            </div>
          </dl>
          <h4>
            技术参数 <span className="cm-caption">声明值，未经能力验证</span>
          </h4>
          {current.data.parameters.length ? (
            <dl className="cm-parameters">
              {current.data.parameters.map((p, i) => (
                <div key={i}>
                  <dt>{p.name}</dt>
                  <dd>
                    {p.value} <span>{p.unit}</span>
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="cm-empty">本版本未填写技术参数。</p>
          )}
          <h4>
            主图与附件 <span className="cm-caption">{current.media.length} 份</span>
          </h4>
          {!current.media.length ? (
            <p className="cm-empty">本版本未关联材料。</p>
          ) : (
            <div className="cm-media-grid">
              {current.media.map((m) => (
                <MediaCard
                  key={m.id}
                  media={m}
                  image={current.images[m.id]}
                  error={current.imageErrors[m.id]}
                  action={
                    <>
                      {m.mime === 'application/pdf' ? (
                        <button
                          type="button"
                          disabled={downloadState?.busy}
                          onClick={() => void download(m)}
                        >
                          {downloadState?.id === m.id && downloadState.busy
                            ? '正在校验文件…'
                            : `下载PDF：${m.name}`}
                        </button>
                      ) : (
                        current.imageErrors[m.id] && (
                          <button type="button" onClick={snapshot.retry}>
                            重试主图读取
                          </button>
                        )
                      )}
                      {downloadState?.id === m.id && downloadState.error && (
                        <p role="alert" className="cm-error">
                          {downloadState.error}
                        </p>
                      )}
                      {downloadState?.id === m.id && downloadState.success && (
                        <p role="status" className="cm-caption">
                          已校验文件并发起下载。
                        </p>
                      )}
                    </>
                  }
                />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

export function ContentChanges({
  before,
  after,
  beforeRevision,
  afterRevision,
}: {
  before?: string | null | undefined;
  after?: string | null | undefined;
  beforeRevision?: number | undefined;
  afterRevision?: number | undefined;
}) {
  if (!contentMediaEnabled()) return null;
  if (!before)
    return (
      <section className="cm-changes" aria-label="版本资料差异">
        <h3>版本变化</h3>
        <p className="cm-empty">
          暂无对照：此前提交版本尚未加载。可刷新审核队列；当前资料不作新增或未变化判断。
        </p>
      </section>
    );
  try {
    if (!after) throw new Error('当前提交内容不可读取');
    const a = parseContent(extensionFromFrames(before)),
      b = parseContent(extensionFromFrames(after));
    const af = contentFrames(before),
      bf = contentFrames(after);
    const rows: { label: string; before: string; after: string }[] = [];
    const asset = (af.length === 12 || af.length === 14) && (bf.length === 12 || bf.length === 14);
    if (asset)
      rows.push(
        { label: '装备用途', before: af[3]!, after: bf[3]! },
        { label: '实际配置声明', before: af[4]!, after: bf[4]! },
      );
    else rows.push({ label: '用途声明', before: a.purpose, after: b.purpose });
    rows.push({ label: '安全与技术限制', before: a.limitations, after: b.limitations });
    // Pair named parameters by occurrence; duplicate names must not hide a change.
    const parameterMap = (data: ContentData) => {
      const counts = new Map<string, number>();
      return new Map(
        data.parameters.map((p) => {
          const n = (counts.get(p.name) ?? 0) + 1;
          counts.set(p.name, n);
          return [
            JSON.stringify([p.name, n]),
            { label: p.name + (n > 1 ? `（${n}）` : ''), value: `${p.value} ${p.unit}`.trim() },
          ];
        }),
      );
    };
    const ap = parameterMap(a),
      bp = parameterMap(b);
    for (const key of new Set([...ap.keys(), ...bp.keys()]))
      rows.push({
        label: (bp.get(key) ?? ap.get(key))!.label,
        before: ap.get(key)?.value ?? '',
        after: bp.get(key)?.value ?? '',
      });
    const same = (m: ContentData['media'][number], n: ContentData['media'][number]) =>
      m.id === n.id && m.sha256 === n.sha256;
    const removed = a.media.filter((m) => !b.media.some((n) => same(m, n))).length,
      added = b.media.filter((m) => !a.media.some((n) => same(m, n))).length;
    const changed = rows.filter((r) => r.before !== r.after);
    return (
      <section className="cm-changes" aria-label="版本资料差异">
        <div className="cm-section-heading">
          <h3>版本变化</h3>
          <span className="cm-badge">
            {changed.length} 项资料变化 ·{' '}
            {added + removed ? `材料 +${added} / −${removed}` : '材料未变化'}
          </span>
        </div>
        <p className="cm-caption">
          对照已加载的此前提交{beforeRevision ? ` r${beforeRevision}` : ''}与本次提交
          {afterRevision ? ` r${afterRevision}` : ''}；此前提交不等同于已发布版本。
        </p>
        {changed.length > 0 ? (
          <div className="cm-diff-table">
            <div className="cm-diff-head">
              <span>变化项目</span>
              <span>此前{beforeRevision ? ` · r${beforeRevision}` : ''}</span>
              <span>本次{afterRevision ? ` · r${afterRevision}` : ''}</span>
            </div>
            {changed.map((r, i) => (
              <div className="cm-diff-row" key={i}>
                <strong>{r.label}</strong>
                <div className="cm-before">
                  <small>此前</small>
                  {r.before || '未填写'}
                </div>
                <div className="cm-after">
                  <small>本次</small>
                  {r.after || '已移除'}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="cm-empty">可对照的文字与技术参数未变化。</p>
        )}
        <div className="cm-media-change">
          <strong>材料清单</strong>
          <span>
            此前 {a.media.length} 份 → 本次 {b.media.length} 份
          </span>
          <span>
            {added + removed
              ? `新增 ${added} 份，移除 ${removed} 份；本次材料见下方主图与附件。`
              : '关联标识与摘要相同。'}
          </span>
        </div>
        <details className="cm-audit">
          <summary>查看未变化项目与材料绑定摘要</summary>
          {rows
            .filter((r) => r.before === r.after)
            .map((r, i) => (
              <p key={i}>
                {r.label}：{r.after || '未填写'}（未变化）
              </p>
            ))}
          <h4>此前材料绑定</h4>
          <pre>{JSON.stringify(a.media, null, 2)}</pre>
          <h4>本次材料绑定</h4>
          <pre>{JSON.stringify(b.media, null, 2)}</pre>
        </details>
      </section>
    );
  } catch {
    return (
      <p role="alert" className="cm-error">
        版本差异格式不可读取；未生成差异结论。
      </p>
    );
  }
}
